package com.mps.asc_app.data.user

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.firestore.firestore
import kotlin.time.Clock

class UserRepositoryImpl : UserRepository {
    private val noMembershipReviewFlagPaths = listOf(
        "manuallyChecked",
        "manualChecked",
        "userManuallyChecked",
        "checkedNoMemberOrPadel",
        "checkedNoMembershipOrPadel",
        "geprueftAberKeinMitgliedOderPadel",
        "system.flags.manuallyChecked",
        "system.flags.manualChecked",
        "system.flags.userManuallyChecked",
        "system.flags.checkedNoMemberOrPadel",
        "system.flags.checkedNoMembershipOrPadel",
        "system.flags.geprueftAberKeinMitgliedOderPadel",
    )
    private val membershipRequestSubmittedFlagPaths = listOf(
        "membershipRequestSubmitted",
        "membershipFormSubmitted",
        "system.flags.membershipRequestSubmitted",
        "system.membershipRequest.submitted",
    )

    internal fun asTruthyFlag(value: Any?): Boolean {
        return when (value) {
            is Boolean -> value
            is Number -> value.toInt() > 0
            is String -> {
                val normalized = value.trim().lowercase()
                normalized == "true" || normalized == "1" || normalized == "ja" || normalized == "yes"
            }
            else -> false
        }
    }

    // Reads a boolean Firestore field, trying Boolean? first to avoid gitlive Any? deserialization issues on KMP/Web.
    private fun readBooleanFlag(doc: DocumentSnapshot, path: String): Boolean {
        runCatching { doc.get<Boolean?>(path) }.getOrNull()?.let { return it }
        runCatching { doc.get<String?>(path) }.getOrNull()?.let { s ->
            val n = s.trim().lowercase()
            if (n == "true" || n == "1" || n == "ja" || n == "yes") return true
            if (n == "false" || n == "0" || n == "nein" || n == "no") return false
        }
        runCatching { doc.get<Long?>(path) }.getOrNull()?.let { return it > 0 }
        val raw = runCatching { doc.get<Any?>(path) }.getOrNull()
        return asTruthyFlag(raw)
    }

    internal fun normalizeEpochMillis(value: Long): Long {
        return when {
            value <= 0L -> value
            value <= 9_999_999_999L -> value * 1000L
            value >= 1_000_000_000_000_000L -> value / 1000L
            else -> value
        }
    }

    private fun readCreatedAtEpochMillis(doc: DocumentSnapshot): Long? {
        runCatching { doc.get<Timestamp?>("createdAt") }.getOrNull()?.let { ts ->
            return normalizeEpochMillis(ts.seconds * 1000L + (ts.nanoseconds / 1_000_000L))
        }

        runCatching { doc.get<Long?>("createdAt") }.getOrNull()?.let { raw ->
            return normalizeEpochMillis(raw)
        }

        runCatching { doc.get<Double?>("createdAt") }.getOrNull()?.toLong()?.let { raw ->
            return normalizeEpochMillis(raw)
        }

        val sec = runCatching { doc.get<Long?>("createdAt.seconds") }.getOrNull()
            ?: runCatching { doc.get<Long?>("createdAt._seconds") }.getOrNull()
        val nanos = runCatching { doc.get<Long?>("createdAt.nanoseconds") }.getOrNull()
            ?: runCatching { doc.get<Long?>("createdAt._nanoseconds") }.getOrNull()
            ?: 0L
        if (sec != null && sec > 0L) {
            return normalizeEpochMillis(sec * 1000L + (nanos / 1_000_000L))
        }

        return null
    }

    internal fun normalizeStringValue(raw: Any?): String? {
        return when (raw) {
            is String -> raw.trim().takeIf { it.isNotBlank() && it != "[object Object]" }
            is Number, is Boolean -> raw.toString()
            is Map<*, *> -> {
                val preferred = listOf("fullText", "formattedAddress", "address", "description", "label")
                    .mapNotNull { key -> (raw[key] as? String)?.trim()?.takeIf { it.isNotBlank() } }
                    .firstOrNull()
                preferred
            }
            else -> null
        }
    }

    private fun readStringField(doc: DocumentSnapshot, field: String): String? {
        return runCatching {
            normalizeStringValue(doc.get<Any?>(field))
        }.getOrElse {
            runCatching { doc.get<String?>(field)?.trim()?.takeIf { it.isNotBlank() } }.getOrNull()
        }
    }

    internal fun normalizeStringList(raw: Any?): List<String> {
        val values = when (raw) {
            is List<*> -> raw
            is String -> listOf(raw)
            else -> emptyList<Any?>()
        }

        return values.mapNotNull { item ->
            when (item) {
                is String -> item.trim()
                else -> item?.toString()?.trim()
            }
        }
            .filter { it.isNotBlank() }
            .distinct()
    }

    private fun readStringListField(doc: DocumentSnapshot, field: String): List<String> {
        return runCatching {
            normalizeStringList(doc.get<Any?>(field))
        }.getOrElse {
            runCatching {
                doc.get<List<String>?>(field)
                    ?.map { it.trim() }
                    ?.filter { it.isNotBlank() }
                    ?.distinct()
                    ?: emptyList()
            }.getOrDefault(emptyList())
        }
    }

    private fun readFirstStringListField(doc: DocumentSnapshot, vararg fields: String): List<String> {
        fields.forEach { field ->
            val value = readStringListField(doc, field)
            if (value.isNotEmpty()) return value
        }
        return emptyList()
    }

    @OptIn(kotlin.time.ExperimentalTime::class)
    private fun nowTimestamp(): Timestamp {
        val nowMs = Clock.System.now().toEpochMilliseconds()
        val seconds = nowMs / 1000
        val nanos = ((nowMs % 1000) * 1_000_000).toInt()
        return Timestamp(seconds = seconds, nanoseconds = nanos)
    }

    internal fun parseRole(rawRole: String?, rawIsAdmin: Boolean?, rawIsTrainer: Boolean?): UserRole {
        if (rawIsAdmin == true) return UserRole.ADMIN
        val normalized = rawRole?.trim()?.lowercase()
        return when {
            normalized == "admin" || normalized == "administrator" -> UserRole.ADMIN
            rawIsTrainer == true -> UserRole.TRAINER
            normalized == "trainer" -> UserRole.TRAINER
            else -> UserRole.MEMBER
        }
    }

    internal fun parseIsTrainer(rawRole: String?, rawIsTrainer: Boolean?): Boolean {
        if (rawIsTrainer == true) return true
        return rawRole?.trim()?.lowercase() == "trainer"
    }

    internal fun resolveDisplayName(
        firstName: String?,
        lastName: String?,
        displayName: String?,
        email: String?
    ): String {
        val fullName = listOf(firstName.orEmpty(), lastName.orEmpty())
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .joinToString(" ")

        if (fullName.isNotBlank()) return fullName
        if (!displayName.isNullOrBlank()) return displayName.trim()
        if (!email.isNullOrBlank()) return email.trim()
        return "Unbekannt"
    }

    private fun parseStatus(rawStatus: String?): UserStatus {
        return when (rawStatus?.trim()?.lowercase()) {
            "approved" -> UserStatus.APPROVED
            "pending" -> UserStatus.PENDING
            "email_not_verified", "email-not-verified" -> UserStatus.EMAIL_NOT_VERIFIED
            null, "" -> UserStatus.PENDING
            else -> UserStatus.PENDING
        }
    }

    internal fun hasBookingDepartment(values: List<String>): Boolean {
        if (values.isEmpty()) return false
        val normalized = values
            .map { it.trim().lowercase() }
            .filter { it.isNotBlank() }
            .toSet()
        return "padel" in normalized || "tennis" in normalized
    }

    internal fun hasPadelDepartment(values: List<String>): Boolean {
        if (values.isEmpty()) return false
        return values.any { it.trim().equals("padel", ignoreCase = true) }
    }

    override suspend fun getApprovalProfile(uid: String): ApprovalProfile {
        val doc = Firebase.firestore.collection("users").document(uid).get()
        if (!doc.exists) return ApprovalProfile(status = UserStatus.PENDING)

        val formTarget = manualReviewFormTargetOf(doc)
        val role = doc.get<String?>("role")
        val explicitIsAdmin = doc.get<Boolean?>("isAdmin")
        val explicitIsTrainer = doc.get<Boolean?>("isTrainer")
        val privacyConsentAccepted = doc.get<Boolean?>("privacyConsentAccepted") == true
        val privacyConsentVersion = doc.get<String?>("privacyConsentVersion")?.trim().orEmpty()

        return ApprovalProfile(
            status = userStatusOf(doc),
            forceAscMembershipForm = formTarget == ManualReviewFormTarget.ASC_MEMBERSHIP,
            forcePadelMembershipForm = formTarget == ManualReviewFormTarget.PADEL_MEMBERSHIP,
            membershipRequestSubmitted = membershipRequestSubmittedFlagPaths.any { path ->
                readBooleanFlag(doc, path)
            },
            isAdmin = parseRole(role, explicitIsAdmin, explicitIsTrainer) == UserRole.ADMIN,
            isTrainer = parseIsTrainer(role, explicitIsTrainer),
            trainerAssignments = readStringListField(doc, "trainerAssignments"),
            acceptedPrivacyConsentVersion = privacyConsentVersion.takeIf { privacyConsentAccepted },
        )
    }

    override suspend fun getUserStatus(uid: String): UserStatus {
        val doc = Firebase.firestore.collection("users").document(uid).get()

        if (!doc.exists) {
            return UserStatus.PENDING
        }

        return userStatusOf(doc)
    }

    private fun userStatusOf(doc: DocumentSnapshot): UserStatus {
        val departments = readFirstStringListField(doc, "departments", "department", "sports", "sparten")
        return if (hasBookingDepartment(departments)) {
            UserStatus.APPROVED
        } else {
            UserStatus.PENDING
        }
    }

    internal enum class ManualReviewFormTarget { NONE, ASC_MEMBERSHIP, PADEL_MEMBERSHIP }

    // Pure decision core (no Firestore access) so the branching can be unit-tested directly.
    internal fun decideManualReviewFormTarget(
        membershipRequestSubmitted: Boolean,
        manualReviewFlagSet: Boolean,
        hasMemberNumber: Boolean,
        hasPadel: Boolean,
    ): ManualReviewFormTarget {
        if (membershipRequestSubmitted) return ManualReviewFormTarget.NONE
        if (!manualReviewFlagSet) return ManualReviewFormTarget.NONE
        if (hasPadel) return ManualReviewFormTarget.NONE

        return if (hasMemberNumber) {
            ManualReviewFormTarget.PADEL_MEMBERSHIP
        } else {
            ManualReviewFormTarget.ASC_MEMBERSHIP
        }
    }

    private suspend fun resolveManualReviewFormTarget(uid: String): ManualReviewFormTarget {
        if (uid.isBlank()) return ManualReviewFormTarget.NONE
        val doc = Firebase.firestore.collection("users").document(uid).get()
        if (!doc.exists) return ManualReviewFormTarget.NONE
        return manualReviewFormTargetOf(doc)
    }

    private fun manualReviewFormTargetOf(doc: DocumentSnapshot): ManualReviewFormTarget {
        val membershipRequestSubmitted = membershipRequestSubmittedFlagPaths.any { path ->
            readBooleanFlag(doc, path)
        }

        val flagIsSet = noMembershipReviewFlagPaths.any { path ->
            readBooleanFlag(doc, path)
        }

        val memberNumber =
            readStringField(doc, "memberNumber")
                ?: readStringField(doc, "membershipNumber")
                ?: readStringField(doc, "memberId")
                ?: readStringField(doc, "mitgliedsnummer")
                ?: ""
        val hasMemberNumber = memberNumber.trim().isNotBlank()

        val sports = readFirstStringListField(doc, "sports", "sparten", "departments", "department")
        val hasPadel = hasPadelDepartment(sports)

        return decideManualReviewFormTarget(
            membershipRequestSubmitted = membershipRequestSubmitted,
            manualReviewFlagSet = flagIsSet,
            hasMemberNumber = hasMemberNumber,
            hasPadel = hasPadel,
        )
    }

    override suspend fun shouldForceAscMembershipForm(uid: String): Boolean {
        return resolveManualReviewFormTarget(uid) == ManualReviewFormTarget.ASC_MEMBERSHIP
    }

    override suspend fun shouldForcePadelMembershipForm(uid: String): Boolean {
        return resolveManualReviewFormTarget(uid) == ManualReviewFormTarget.PADEL_MEMBERSHIP
    }

    override suspend fun hasMembershipRequestSubmitted(uid: String): Boolean {
        if (uid.isBlank()) return false
        val doc = Firebase.firestore.collection("users").document(uid).get()
        if (!doc.exists) return false

        return membershipRequestSubmittedFlagPaths.any { path ->
            readBooleanFlag(doc, path)
        }
    }

    override suspend fun isAdmin(uid: String): Boolean {
        if (uid.isBlank()) return false
        val doc = Firebase.firestore.collection("users").document(uid).get()
        if (!doc.exists) return false

        val role = doc.get<String?>("role")
        val explicitIsAdmin = doc.get<Boolean?>("isAdmin")
        val explicitIsTrainer = doc.get<Boolean?>("isTrainer")
        return parseRole(role, explicitIsAdmin, explicitIsTrainer) == UserRole.ADMIN
    }

    override suspend fun isTrainer(uid: String): Boolean {
        if (uid.isBlank()) return false
        val doc = Firebase.firestore.collection("users").document(uid).get()
        if (!doc.exists) return false

        val role = doc.get<String?>("role")
        val explicitIsTrainer = doc.get<Boolean?>("isTrainer")
        return parseIsTrainer(role, explicitIsTrainer)
    }

    override suspend fun getUserRole(uid: String): UserRole {
        if (uid.isBlank()) return UserRole.MEMBER
        val doc = Firebase.firestore.collection("users").document(uid).get()
        if (!doc.exists) return UserRole.MEMBER
        val role = doc.get<String?>("role")
        val explicitIsAdmin = doc.get<Boolean?>("isAdmin")
        val explicitIsTrainer = doc.get<Boolean?>("isTrainer")
        return parseRole(role, explicitIsAdmin, explicitIsTrainer)
    }

    override suspend fun getTrainerAssignments(uid: String): List<String> {
        if (uid.isBlank()) return emptyList()
        val doc = Firebase.firestore.collection("users").document(uid).get()
        if (!doc.exists) return emptyList()
        return readStringListField(doc, "trainerAssignments")
    }

    override suspend fun getAllMembers(): List<MemberSummary> {
        val snap = Firebase.firestore.collection("users").get()

        return snap.documents.map { doc ->
            val uid = doc.id
            val createdAtEpochMillis = readCreatedAtEpochMillis(doc)

            val firstName = doc.get<String?>("firstName")
            val lastName = doc.get<String?>("lastName")
            val displayName = doc.get<String?>("displayName")
            val email = doc.get<String?>("email")
            val role = doc.get<String?>("role")
            val explicitIsAdmin = doc.get<Boolean?>("isAdmin")
            val explicitIsTrainer = doc.get<Boolean?>("isTrainer")
            val trainerAssignments = readStringListField(doc, "trainerAssignments")
            val memberNumber =
                readStringField(doc, "memberNumber")
                    ?: readStringField(doc, "membershipNumber")
                    ?: readStringField(doc, "memberId")
                    ?: readStringField(doc, "mitgliedsnummer")
                    ?: ""
            val sports = readFirstStringListField(doc, "sports", "sparten", "departments", "department")
            val status = if (hasBookingDepartment(sports)) UserStatus.APPROVED else UserStatus.PENDING
            val privacyConsentAccepted = doc.get<Boolean?>("privacyConsentAccepted") == true
            val parsedRole = parseRole(role, explicitIsAdmin, explicitIsTrainer)
            val parsedIsTrainer = parseIsTrainer(role, explicitIsTrainer)

            MemberSummary(
                uid = uid,
                displayName = resolveDisplayName(firstName, lastName, displayName, email),
                email = email?.trim().orEmpty(),
                createdAtEpochMillis = createdAtEpochMillis,
                memberNumber = memberNumber,
                sports = sports,
                status = status,
                isAdmin = parsedRole == UserRole.ADMIN,
                isTrainer = parsedIsTrainer,
                trainerAssignments = trainerAssignments,
                privacyConsentAccepted = privacyConsentAccepted,
            )
        }
    }

    override suspend fun getMemberProfile(uid: String): MemberProfile {
        val cleanedUid = uid.trim()
        require(cleanedUid.isNotBlank()) { "uid required" }

        val doc = Firebase.firestore.collection("users").document(cleanedUid).get()
        if (!doc.exists) {
            throw IllegalStateException("User nicht gefunden")
        }

        val firstName = doc.get<String?>("firstName")?.trim().orEmpty()
        val lastName = doc.get<String?>("lastName")?.trim().orEmpty()
        val displayName = doc.get<String?>("displayName")?.trim()
        val email = doc.get<String?>("email")?.trim().orEmpty()

        val memberNumber =
            readStringField(doc, "memberNumber")
                ?: readStringField(doc, "membershipNumber")
                ?: readStringField(doc, "memberId")
                ?: readStringField(doc, "mitgliedsnummer")
                ?: ""

        val phone =
            readStringField(doc, "phone")
                ?: readStringField(doc, "phoneNumber")
                ?: readStringField(doc, "telefon")
                ?: ""

        val addressText =
            readStringField(doc, "address")
                ?: readStringField(doc, "addressQuery")
                ?: readStringField(doc, "fullAddress")
                ?: ""

        val placeId = readStringField(doc, "placeId")

        val sports = readFirstStringListField(doc, "sports", "sparten", "departments", "department")

        val role = doc.get<String?>("role")
        val explicitIsAdmin = doc.get<Boolean?>("isAdmin")
        val explicitIsTrainer = doc.get<Boolean?>("isTrainer")
        val trainerAssignments = readStringListField(doc, "trainerAssignments")
        val manuallyChecked = doc.get<Boolean?>("manuallyChecked") ?: false
        val parsedRole = parseRole(role, explicitIsAdmin, explicitIsTrainer)
        val parsedIsTrainer = parseIsTrainer(role, explicitIsTrainer)
        val status = if (hasBookingDepartment(sports)) UserStatus.APPROVED else UserStatus.PENDING

        // displayName ist nicht editierbar, aber wir nutzen ihn als Fallback für Vor/Nachnamen.
        val resolvedDisplay = resolveDisplayName(firstName, lastName, displayName, email)
        val fallbackParts = resolvedDisplay.split(" ").map { it.trim() }.filter { it.isNotBlank() }

        return MemberProfile(
            uid = cleanedUid,
            email = email,
            firstName = firstName.ifBlank { fallbackParts.firstOrNull().orEmpty() },
            lastName = lastName.ifBlank { fallbackParts.drop(1).joinToString(" ") },
            memberNumber = memberNumber,
            phone = phone,
            addressText = addressText,
            placeId = placeId,
            sports = sports,
            status = status,
            isAdmin = parsedRole == UserRole.ADMIN,
            isTrainer = parsedIsTrainer,
            trainerAssignments = trainerAssignments,
            manuallyChecked = manuallyChecked,
        )
    }

    override suspend fun getTrainingGroups(): List<TrainingGroup> {
        val snap = Firebase.firestore.collection("training_groups").get()
        return snap.documents.mapNotNull { doc ->
            val name = doc.get<String?>("name")?.trim().orEmpty()
            val rawType = doc.get<String?>("type")?.trim()?.lowercase().orEmpty()
            val type = when (rawType) {
                "team" -> TrainingGroupType.TEAM
                "year" -> TrainingGroupType.YEAR
                else -> return@mapNotNull null
            }
            if (name.isBlank()) return@mapNotNull null
            TrainingGroup(id = doc.id, name = name, type = type)
        }.sortedWith(compareBy<TrainingGroup> { it.type.name }.thenBy { it.name.lowercase() })
    }

    override suspend fun createTrainingGroup(name: String, type: TrainingGroupType): Result<Unit> = runCatching {
        val cleanedName = name.trim()
        require(cleanedName.isNotBlank()) { "Name erforderlich" }

        val normalizedName = cleanedName.lowercase()
        val typeValue = when (type) {
            TrainingGroupType.TEAM -> "team"
            TrainingGroupType.YEAR -> "year"
        }

        val groupId = "${typeValue}:${normalizedName}"

        Firebase.firestore.collection("training_groups").document(groupId).set(
            mapOf(
                "name" to cleanedName,
                "normalizedName" to normalizedName,
                "type" to typeValue,
                "createdAt" to nowTimestamp(),
            ),
            merge = false,
        )
    }

    override suspend fun deleteTrainingGroup(groupId: String): Result<Unit> = runCatching {
        val cleaned = groupId.trim()
        require(cleaned.isNotBlank()) { "groupId erforderlich" }

        Firebase.firestore.collection("training_groups").document(cleaned).delete()

        // Referenzen bei Trainern bereinigen.
        val usersSnap = Firebase.firestore.collection("users").get()
        usersSnap.documents.forEach { userDoc ->
            val current = readStringListField(userDoc, "trainerAssignments")
            if (cleaned in current) {
                val next = current.filterNot { it == cleaned }
                Firebase.firestore.collection("users").document(userDoc.id)
                    .set(mapOf("trainerAssignments" to next), merge = true)
            }
        }
    }

    override suspend fun updateMemberProfile(
        uid: String,
        firstName: String,
        lastName: String,
        memberNumber: String,
        phone: String,
        addressText: String,
        placeId: String?,
        sports: List<String>,
        isAdmin: Boolean,
        isTrainer: Boolean,
        trainerAssignments: List<String>,
        manuallyChecked: Boolean,
    ) {
        val cleanedUid = uid.trim()
        require(cleanedUid.isNotBlank()) { "uid required" }

        val cleanedFirst = firstName.trim()
        val cleanedLast = lastName.trim()
        val cleanedDisplayName = listOf(cleanedFirst, cleanedLast)
            .filter { it.isNotBlank() }
            .joinToString(" ")

        val cleanedMemberNumber = memberNumber.trim()
        val cleanedPhone = phone.trim()
        val cleanedAddress = addressText.trim().let { value ->
            if (value == "[object Object]") "" else value
        }
        val cleanedPlaceId = placeId?.trim().orEmpty()

        val cleanedSports = sports
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .distinct()

        val cleanedTrainerAssignments = trainerAssignments
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .distinct()

        val roleString = when {
            isAdmin -> "admin"
            isTrainer -> "trainer"
            else -> "user"
        }

        val sportsValue: Any? = cleanedSports
            .takeIf { it.isNotEmpty() }
            ?.toList()

        val data = mutableMapOf<String, Any?>(
            "firstName" to cleanedFirst,
            "lastName" to cleanedLast,
            "displayName" to cleanedDisplayName,
            "memberNumber" to cleanedMemberNumber,
            "membershipNumber" to cleanedMemberNumber,
            "phone" to cleanedPhone,
            "address" to cleanedAddress,
            "placeId" to cleanedPlaceId,
            "sports" to sportsValue,
            "departments" to sportsValue,
            "isAdmin" to isAdmin,
            "isTrainer" to isTrainer,
            "role" to roleString,
            "manuallyChecked" to manuallyChecked,
        )

        if (isTrainer) {
            data["trainerAssignments"] = cleanedTrainerAssignments.toList()
        }

        Firebase.firestore.collection("users").document(cleanedUid)
            .set(data, merge = true)
    }

    override suspend fun hasAcceptedPrivacyConsent(uid: String, requiredVersion: String): Boolean {
        val cleanedUid = uid.trim()
        if (cleanedUid.isBlank()) return false

        val doc = Firebase.firestore.collection("users").document(cleanedUid).get()
        if (!doc.exists) return false

        val accepted = doc.get<Boolean?>("privacyConsentAccepted") == true
        val version = doc.get<String?>("privacyConsentVersion")?.trim().orEmpty()

        return accepted && version == requiredVersion
    }

    override suspend fun acceptPrivacyConsent(uid: String, requiredVersion: String): Result<Unit> = runCatching {
        val cleanedUid = uid.trim()
        require(cleanedUid.isNotBlank()) { "uid required" }

        val data = mapOf(
            "privacyConsentAccepted" to true,
            "privacyConsentVersion" to requiredVersion,
            "privacyConsentAcceptedAt" to nowTimestamp(),
        )

        Firebase.firestore.collection("users").document(cleanedUid)
            .set(data, merge = true)
    }
}